import { useEffect, useMemo, useState } from 'react'
import { useUI } from '@/store/ui'
import { AuthAPI } from '@/api/auth'

// Pre-launch registration gate, frontend side. One probe (GET /auth/gate/status,
// which relays DIWA_REGISTRATION_GATED) drives every "create account" surface:
// the landing CTAs, the marketing nav, the /signup door, and the login page's
// signup toggle. Flipping the flag at launch (May 1, 2027) opens all of them at once —
// config, not a frontend deploy.

/** Tri-state: `undefined` while the probe is in flight, then true/false.
 *  A failed probe reports closed — pre-launch, the waitlist story is the safe
 *  fallback (an open-signup story pointing at a gated backend would 403). */
export function useRegistrationStatus(): boolean | undefined {
  const diwaUrl = useUI((s) => s.diwaUrl)
  const api = useMemo(() => new AuthAPI(diwaUrl), [diwaUrl])
  const [open, setOpen] = useState<boolean | undefined>(undefined)
  useEffect(() => {
    let alive = true
    api
      .gateStatus()
      .then((g) => alive && setOpen(g.registration_open === true))
      .catch(() => alive && setOpen(false))
    return () => {
      alive = false
    }
  }, [api])
  return open
}

/** Two-state convenience for marketing surfaces: closed until proven open,
 *  so the pre-launch story never flashes an open-signup CTA. */
export function useRegistrationOpen(): boolean {
  return useRegistrationStatus() === true
}
